import React, { useEffect, useState, useRef } from 'react';
import { Mail, MessageCircle, Copy, Check, Send, Phone, AlertCircle, Users, Zap, X, Clock, RefreshCw, Bolt } from 'lucide-react';
import { api } from '../lib/api';
import { Lead, Template, UserCategory } from '../types';
import { CategoryBadge, TypeBadge, Spinner, useToast, Toast } from '../components/UI';

function replacePH(text: string, lead: Lead): string {
  return text
    .replace(/\{\{company_name\}\}/g, lead.company)
    .replace(/\{\{contact_email\}\}/g, lead.email)
    .replace(/\{\{contact_phone\}\}/g, lead.phone || '')
    .replace(/\{\{category\}\}/g, lead.category);
}

function formatEta(isoString: string): string {
  const diff = new Date(isoString).getTime() - Date.now();
  if (diff <= 0) return 'finishing…';
  const secs = Math.ceil(diff / 1000);
  if (secs < 60) return `~${secs}s remaining`;
  const mins = Math.ceil(secs / 60);
  return `~${mins}m remaining`;
}

function formatDuration(start: string, end: string): string {
  const diff = new Date(end).getTime() - new Date(start).getTime();
  const secs = Math.round(diff / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

// ── Single Send ───────────────────────────────────────────────────────────────
function SingleSend({ leads, templates, categories }: { leads: Lead[]; templates: Template[]; categories: UserCategory[] }) {
  const { toast, show } = useToast();
  const [selectedLead, setSelectedLead] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');

  const lead = leads.find(l => l.id === selectedLead) ?? null;
  const template = templates.find(t => t.id === selectedTemplate) ?? null;
  const resolvedSubject = template && lead ? replacePH(template.subject, lead) : template?.subject ?? '';
  const resolvedBody = template && lead ? replacePH(template.body, lead) : template?.body ?? '';
  const ready = lead !== null && template !== null;
  const getCatColor = (n: string) => categories.find(c => c.name === n)?.color;

  const sendEmail = async () => {
    if (!ready) return;
    setSending(true);
    try {
      await api.sendEmail({ to: lead!.email, subject: resolvedSubject, body: resolvedBody, leadId: lead!.id });
      show(`Email sent to ${lead!.email}`, 'success');
    } catch (e: any) { show(e.message || 'Failed to send email', 'error'); }
    finally { setSending(false); }
  };

  const sendWhatsApp = async () => {
    if (!ready || !lead?.phone) { show('No phone number', 'error'); return; }
    setSending(true);
    try {
      await api.sendWhatsApp({ to: lead!.phone, body: resolvedBody, leadId: lead!.id });
      show(`WhatsApp sent to ${lead!.phone}`, 'success');
    } catch (e: any) { show(e.message || 'Failed to send', 'error'); }
    finally { setSending(false); }
  };

  const openWhatsAppApp = () => {
    if (!ready || !lead?.phone) { show('No phone number', 'error'); return; }
    window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=${encodeURIComponent(resolvedBody)}`, '_blank');
    show('Opening WhatsApp…', 'info');
  };

  const copy = () => {
    navigator.clipboard.writeText(resolvedBody).then(() => {
      setCopied(true); show('Copied!', 'success');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const filteredLeads = leads.filter(l =>
    !leadSearch || l.company.toLowerCase().includes(leadSearch.toLowerCase()) || l.email.toLowerCase().includes(leadSearch.toLowerCase())
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Lead picker */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>1</div>
            Choose Lead
          </div>
          <input placeholder="Search leads…" value={leadSearch} onChange={e => setLeadSearch(e.target.value)} style={{ marginBottom: 10 }} />
          <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {filteredLeads.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>No leads found</div>}
            {filteredLeads.map(l => {
              const active = selectedLead === l.id;
              return (
                <div key={l.id} onClick={() => setSelectedLead(l.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${active ? 'var(--accent)' : 'transparent'}`, background: active ? 'rgba(108,99,255,0.08)' : 'transparent', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: `${getCatColor(l.category) || 'var(--accent)'}18`, color: getCatColor(l.category) || 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{l.company.charAt(0)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.company}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.email}</div>
                  </div>
                  <CategoryBadge name={l.category} color={getCatColor(l.category)} />
                  {active && <Check size={13} color="var(--accent)" />}
                </div>
              );
            })}
          </div>
        </div>
        {/* Template picker */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>2</div>
            Choose Template
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {templates.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>No templates — create one first</div>}
            {templates.map(t => {
              const active = selectedTemplate === t.id;
              const color = { email: 'var(--accent)', whatsapp: 'var(--accent3)', instagram: 'var(--accent2)' }[t.type];
              return (
                <div key={t.id} onClick={() => setSelectedTemplate(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'transparent'}`, background: active ? `${color}08` : 'transparent', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ color, flexShrink: 0 }}>{t.type === 'email' ? <Mail size={14} /> : t.type === 'whatsapp' ? <MessageCircle size={14} /> : <Copy size={14} />}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                    {t.subject && <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>}
                  </div>
                  <TypeBadge type={t.type} />
                  {active && <Check size={13} color={color} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="card" style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>3</div>
            Preview & Send
          </div>
          {!ready ? (
            <div className="empty-state" style={{ padding: '40px 0' }}><Send size={28} /><p>Select a lead and template to preview</p></div>
          ) : (
            <>
              <div style={{ background: 'var(--surface2)', borderRadius: 9, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: `${getCatColor(lead!.category) || 'var(--accent)'}18`, color: getCatColor(lead!.category) || 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15 }}>{lead!.company.charAt(0)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{lead!.company}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 10 }}>
                    <span>{lead!.email}</span>
                    {lead!.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Phone size={10} />{lead!.phone}</span>}
                  </div>
                </div>
                <CategoryBadge name={lead!.category} color={getCatColor(lead!.category)} />
              </div>
              {resolvedSubject && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Subject</div><div style={{ fontSize: 13, fontWeight: 500 }}>{resolvedSubject}</div></div>}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Message</div>
              <div className="preview-box" style={{ minHeight: 120 }}>{resolvedBody}</div>
              {!lead!.phone && template!.type === 'whatsapp' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 8, padding: '9px 12px', marginTop: 12, fontSize: 12, color: 'var(--accent2)' }}>
                  <AlertCircle size={13} /> This lead has no phone number.
                </div>
              )}
            </>
          )}
        </div>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-full" onClick={sendEmail} disabled={!ready || sending || template?.type !== 'email'}>
            {sending ? <Spinner size={14} /> : <Mail size={14} />} Send Email
            {template && template.type !== 'email' && <span style={{ fontSize: 11, opacity: 0.6 }}>(email template required)</span>}
          </button>
          <button onClick={sendWhatsApp} disabled={!ready || sending || template?.type !== 'whatsapp'} style={{ background: 'var(--accent3)', color: '#fff', border: 'none', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 18px', fontFamily: 'DM Sans', fontSize: 14, fontWeight: 500, cursor: (!ready || sending || template?.type !== 'whatsapp') ? 'not-allowed' : 'pointer', opacity: (!ready || sending || template?.type !== 'whatsapp') ? 0.45 : 1, transition: 'all 0.18s' }}>
            {sending ? <Spinner size={14} /> : <MessageCircle size={14} />} Send via WhatsApp API
          </button>
          <button className="btn btn-ghost btn-full" onClick={openWhatsAppApp} disabled={!ready || !lead?.phone}><MessageCircle size={14} /> Open WhatsApp App</button>
          <button className="btn btn-ghost btn-full" onClick={copy} disabled={!ready}>
            {copied ? <Check size={14} color="var(--accent3)" /> : <Copy size={14} />}{copied ? 'Copied!' : 'Copy Message'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mass Send ─────────────────────────────────────────────────────────────────
function MassSend({ leads, templates, categories }: { leads: Lead[]; templates: Template[]; categories: UserCategory[] }) {
  const { toast, show } = useToast();
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email');
  const [delay, setDelay] = useState(1500);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<any | null>(null);
  const [launching, setLaunching] = useState(false);
  const [eta, setEta] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const template = templates.find(t => t.id === selectedTemplate) ?? null;
  const getCatColor = (n: string) => categories.find(c => c.name === n)?.color;

  const targetLeads = selectedCats.length === 0 ? leads : leads.filter(l => selectedCats.includes(l.category));
  const validLeads = channel === 'email' ? targetLeads.filter(l => l.email) : targetLeads.filter(l => l.phone);
  const skippedCount = targetLeads.length - validLeads.length;
  const canLaunch = validLeads.length > 0 && template !== null && !launching && !campaignId;

  const toggleCat = (cat: string) => setSelectedCats(p => p.includes(cat) ? p.filter(c => c !== cat) : [...p, cat]);

  // Poll campaign status while running
  useEffect(() => {
    if (!campaignId) return;
    const poll = async () => {
      try {
        const data = await api.getCampaign(campaignId);
        setCampaign(data);
        if (data.estimatedFinishAt) setEta(formatEta(data.estimatedFinishAt));
        if (data.status === 'done' || data.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setEta('');
          show(data.status === 'done' ? `Campaign complete — ${data.sent} sent, ${data.failed} failed` : 'Campaign failed', data.status === 'done' ? 'success' : 'error');
        }
      } catch (e) { /* ignore poll errors */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [campaignId]);

  // Update ETA display every second
  useEffect(() => {
    if (!campaign?.estimatedFinishAt || campaign?.status !== 'running') return;
    const t = setInterval(() => setEta(formatEta(campaign.estimatedFinishAt)), 1000);
    return () => clearInterval(t);
  }, [campaign?.estimatedFinishAt, campaign?.status]);

  const launch = async () => {
    if (!canLaunch || !template) return;
    setLaunching(true);
    try {
      const { id } = await api.createCampaign({
        templateId: template.id,
        leadIds: validLeads.map(l => l.id),
        channel,
        delayMs: delay,
      });
      setCampaignId(id);
    } catch (e: any) {
      show(e.message || 'Failed to launch campaign', 'error');
    } finally {
      setLaunching(false);
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setCampaignId(null); setCampaign(null); setEta('');
  };

  const running = campaign?.status === 'running' || campaign?.status === 'queued';
  const done = campaign?.status === 'done';
  const progressPct = campaign ? Math.round(((campaign.sent + campaign.failed) / campaign.total) * 100) : 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      {/* Left: Config */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Channel */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>1</div>
            Channel
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['email', 'whatsapp'] as const).map(ch => (
              <button key={ch} onClick={() => { setChannel(ch); setSelectedTemplate(''); reset(); }} style={{ flex: 1, padding: '10px', borderRadius: 9, border: `1px solid ${channel === ch ? (ch === 'email' ? 'var(--accent)' : 'var(--accent3)') : 'var(--border)'}`, background: channel === ch ? (ch === 'email' ? 'rgba(108,99,255,0.1)' : 'rgba(37,211,102,0.1)') : 'transparent', color: channel === ch ? (ch === 'email' ? 'var(--accent)' : 'var(--accent3)') : 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontWeight: 600, fontSize: 13, fontFamily: 'DM Sans', transition: 'all 0.15s' }}>
                {ch === 'email' ? <Mail size={14} /> : <MessageCircle size={14} />}
                {ch === 'email' ? 'Email' : 'WhatsApp'}
              </button>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>2</div>
            Filter by Category
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>Leave all unselected to target every lead</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {categories.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>No categories — add in Settings</div>}
            {categories.map(cat => {
              const active = selectedCats.includes(cat.name);
              return (
                <button key={cat.id} onClick={() => { toggleCat(cat.name); reset(); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 13px', borderRadius: 100, border: `1px solid ${active ? cat.color : 'var(--border)'}`, background: active ? `${cat.color}15` : 'transparent', color: active ? cat.color : 'var(--text2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans', transition: 'all 0.15s' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color }} />{cat.name}{active && <Check size={11} />}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12 }}><span style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 18 }}>{validLeads.length}</span><span style={{ color: 'var(--text2)', marginLeft: 5 }}>will receive</span></div>
            {skippedCount > 0 && <div style={{ fontSize: 12 }}><span style={{ fontWeight: 700, color: 'var(--text3)', fontSize: 18 }}>{skippedCount}</span><span style={{ color: 'var(--text3)', marginLeft: 5 }}>skipped (no {channel === 'email' ? 'email' : 'phone'})</span></div>}
          </div>
        </div>

        {/* Template */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, background: 'var(--accent-dim)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: 12, fontWeight: 800 }}>3</div>
            Choose Template
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {templates.filter(t => t.type === channel).length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '16px 0' }}>No {channel} templates — create one first</div>}
            {templates.filter(t => t.type === channel).map(t => {
              const active = selectedTemplate === t.id;
              const color = channel === 'email' ? 'var(--accent)' : 'var(--accent3)';
              return (
                <div key={t.id} onClick={() => { setSelectedTemplate(t.id); reset(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'transparent'}`, background: active ? `${color}08` : 'transparent', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <div style={{ color, flexShrink: 0 }}>{channel === 'email' ? <Mail size={14} /> : <MessageCircle size={14} />}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                    {t.subject && <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>}
                  </div>
                  {active && <Check size={13} color={color} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Delay */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Send Delay</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Gap between messages — longer = less chance of spam flags</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[500, 1500, 3000, 5000].map(d => (
              <button key={d} onClick={() => setDelay(d)} style={{ flex: 1, padding: '7px 4px', borderRadius: 7, border: `1px solid ${delay === d ? 'var(--accent)' : 'var(--border)'}`, background: delay === d ? 'rgba(108,99,255,0.1)' : 'transparent', color: delay === d ? 'var(--accent)' : 'var(--text2)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans', transition: 'all 0.15s' }}>
                {d < 1000 ? `${d}ms` : `${d / 1000}s`}
              </button>
            ))}
          </div>
          {validLeads.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Clock size={11} /> Estimated total: ~{Math.ceil((validLeads.length * delay) / 1000)}s for {validLeads.length} messages
            </div>
          )}
        </div>
      </div>

      {/* Right: Preview + Launch + Results */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Template preview */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Message Preview</div>
          {!template ? (
            <div className="empty-state" style={{ padding: '28px 0' }}><Send size={24} /><p>Select a template to preview</p></div>
          ) : (
            <>
              {template.subject && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Subject</div><div style={{ fontSize: 13, fontWeight: 500 }}>{template.subject}</div></div>}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Body</div>
              <div className="preview-box" style={{ minHeight: 90, fontSize: 13 }}>{template.body}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>Placeholders filled per lead automatically</div>
            </>
          )}
        </div>

        {/* Launch / Progress */}
        {!campaignId ? (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
              {canLaunch ? <>Ready to send <strong style={{ color: 'var(--text)' }}>{validLeads.length} {channel === 'email' ? 'emails' : 'WhatsApp messages'}</strong>{selectedCats.length > 0 && <> to <strong style={{ color: 'var(--text)' }}>{selectedCats.join(', ')}</strong></>} using <strong style={{ color: 'var(--text)' }}>{template?.name}</strong>.<br /><span style={{ fontSize: 12, color: 'var(--text3)' }}>Runs on the server — safe to close this tab.</span></> : 'Select channel, categories (optional), and a template to get started.'}
            </div>
            <button className="btn btn-primary btn-full" onClick={launch} disabled={!canLaunch || launching} style={{ padding: '13px', fontSize: 15, fontWeight: 700 }}>
              {launching ? <Spinner size={16} /> : <Zap size={16} fill="currentColor" />}
              Launch Campaign — {validLeads.length} {channel === 'email' ? 'emails' : 'messages'}
            </button>
          </div>
        ) : (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>
                {campaign?.status === 'queued' && 'Campaign queued…'}
                {campaign?.status === 'running' && 'Campaign running'}
                {campaign?.status === 'done' && '✓ Campaign complete'}
                {campaign?.status === 'failed' && '✗ Campaign failed'}
                {!campaign && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Spinner size={14} /> Starting…</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {running && <div style={{ fontSize: 12, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4 }}><RefreshCw size={11} className="spin" />{eta || 'calculating…'}</div>}
                {done && campaign?.startedAt && campaign?.finishedAt && <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} />{formatDuration(campaign.startedAt, campaign.finishedAt)}</div>}
                {done && <button className="btn btn-ghost btn-sm" onClick={reset}><X size={12} /> New</button>}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { label: 'Sent', val: campaign?.sent ?? 0, color: 'var(--accent3)' },
                { label: 'Failed', val: campaign?.failed ?? 0, color: 'var(--accent2)' },
                { label: 'Pending', val: campaign ? campaign.total - campaign.sent - campaign.failed : 0, color: 'var(--text3)' },
              ].map(({ label, val, color }) => (
                <div key={label} style={{ flex: 1, background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Syne', fontSize: 22, fontWeight: 700, color }}>{val}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            {campaign && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 5 }}>
                  <span>{progressPct}% complete</span>
                  <span>{campaign.sent + campaign.failed} / {campaign.total}</span>
                </div>
                <div style={{ height: 6, background: 'var(--surface3)', borderRadius: 100, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 100, background: done ? 'var(--accent3)' : 'var(--accent)', width: `${progressPct}%`, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            )}

            {/* Per-lead results */}
            {campaign?.results && (
              <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {campaign.results.map((r: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: 'var(--surface2)' }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--surface3)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{(r.company || '?').charAt(0)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.company}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.to}</div>
                    </div>
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {r.status === 'pending' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--border2)' }} />}
                      {r.status === 'sent' && <Check size={14} color="var(--accent3)" />}
                      {r.status === 'failed' && (
                        <>
                          <AlertCircle size={13} color="var(--accent2)" />
                          {r.error && <span style={{ fontSize: 10, color: 'var(--accent2)', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.error}>{r.error}</span>}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Quick Send ────────────────────────────────────────────────────────────────
function QuickSend({ templates }: { templates: Template[] }) {
  const { toast, show } = useToast();
  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const template = templates.find(t => t.id === selectedTemplate) ?? null;
  const filteredTemplates = templates.filter(t => t.type === channel);

  const ready = !!companyName.trim() && !!selectedTemplate &&
    (channel === 'email' ? !!email.trim() : !!phone.trim());

  const send = async () => {
    if (!ready) return;
    setSending(true);
    try {
      const result = await api.quickSend({ companyName, email, phone, templateId: selectedTemplate, channel });
      setSent(true);
      show(`Sent to ${channel === 'email' ? email : phone} — lead saved`, 'success');
      // Reset after success
      setTimeout(() => {
        setCompanyName(''); setEmail(''); setPhone('');
        setSelectedTemplate(''); setSent(false);
      }, 2500);
    } catch (e: any) { show(e.message || 'Failed to send', 'error'); }
    finally { setSending(false); }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', background: 'rgba(108,99,255,0.06)', border: '1px solid rgba(108,99,255,0.15)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Zap size={13} color="var(--accent)" />
          Fill in the details, pick a template and send — the lead gets added to your pipeline automatically.
        </div>

        {/* Channel toggle */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Channel</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['email', 'whatsapp'] as const).map(ch => (
              <button key={ch} onClick={() => { setChannel(ch); setSelectedTemplate(''); }} style={{
                flex: 1, padding: '10px', borderRadius: 9,
                border: `1px solid ${channel === ch ? (ch === 'email' ? 'var(--accent)' : 'var(--accent3)') : 'var(--border)'}`,
                background: channel === ch ? (ch === 'email' ? 'rgba(108,99,255,0.1)' : 'rgba(37,211,102,0.1)') : 'transparent',
                color: channel === ch ? (ch === 'email' ? 'var(--accent)' : 'var(--accent3)') : 'var(--text2)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 7, fontWeight: 600, fontSize: 13, fontFamily: 'DM Sans', transition: 'all 0.15s',
              }}>
                {ch === 'email' ? <Mail size={14} /> : <MessageCircle size={14} />}
                {ch === 'email' ? 'Email' : 'WhatsApp'}
              </button>
            ))}
          </div>
        </div>

        {/* Company name */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Company Name *</label>
          <input placeholder="Acme Corp" value={companyName} onChange={e => setCompanyName(e.target.value)} />
        </div>

        {/* Email or Phone */}
        {channel === 'email' ? (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label><Mail size={11} style={{ marginRight: 4 }} />Email Address *</label>
            <input type="email" placeholder="contact@company.com" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
        ) : (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label><Phone size={11} style={{ marginRight: 4 }} />Phone Number *</label>
            <input placeholder="+263 77 123 4567" value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
        )}

        {/* Template picker */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Template *</label>
          {filteredTemplates.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>No {channel} templates — create one in Templates first</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {filteredTemplates.map(t => {
                const active = selectedTemplate === t.id;
                const color = channel === 'email' ? 'var(--accent)' : 'var(--accent3)';
                return (
                  <div key={t.id} onClick={() => setSelectedTemplate(t.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                    borderRadius: 9, border: `1px solid ${active ? color : 'var(--border)'}`,
                    background: active ? `${color}08` : 'var(--surface2)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                    <div style={{ color, flexShrink: 0 }}>{channel === 'email' ? <Mail size={14} /> : <MessageCircle size={14} />}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                      {t.subject && <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>}
                    </div>
                    {active && <Check size={13} color={color} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Preview snippet */}
        {template && companyName && (
          <div style={{ background: 'var(--surface2)', borderRadius: 9, padding: '12px 14px', borderLeft: `3px solid ${channel === 'email' ? 'var(--accent)' : 'var(--accent3)'}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Preview</div>
            {template.subject && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{template.subject.replace(/\{\{company_name\}\}/g, companyName)}</div>}
            <div style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>
              {template.body.replace(/\{\{company_name\}\}/g, companyName).replace(/\{\{contact_email\}\}/g, email).replace(/\{\{contact_phone\}\}/g, phone)}
            </div>
          </div>
        )}

        {/* Send button */}
        <button onClick={send} disabled={!ready || sending} style={{
          background: sent ? 'var(--accent3)' : channel === 'email' ? 'var(--accent)' : 'var(--accent3)',
          color: '#fff', border: 'none', borderRadius: 10, padding: '14px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700,
          cursor: (!ready || sending) ? 'not-allowed' : 'pointer',
          opacity: (!ready || sending) ? 0.5 : 1, transition: 'all 0.2s',
          boxShadow: ready ? `0 4px 20px ${channel === 'email' ? 'rgba(108,99,255,0.3)' : 'rgba(37,211,102,0.3)'}` : 'none',
        }}>
          {sending ? <><Spinner size={16} /> Sending…</>
          : sent ? <><Check size={16} fill="currentColor" /> Sent & Lead Saved!</>
          : <>{channel === 'email' ? <Mail size={16} /> : <MessageCircle size={16} />} Send & Save Lead</>}
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SendPage(): React.ReactElement {
  const [tab, setTab] = useState<'quick' | 'single' | 'mass'>('quick');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getLeads(), api.getTemplates(), api.getCategories()])
      .then(([l, t, c]) => { setLeads(l); setTemplates(t); setCategories(c); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page" style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}><Spinner size={28} /></div>;

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Send Message</h1>
          <p className="page-sub">Single outreach or bulk campaigns by category</p>
        </div>
        {tab === 'mass' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 8, padding: '7px 12px', fontSize: 12, color: 'var(--accent2)' }}>
            <AlertCircle size={13} /> Uses your SMTP / WhatsApp API credits
          </div>
        )}
      </div>

      <div className="tab-strip">
        <button className={`tab ${tab === 'quick' ? 'active' : ''}`} onClick={() => setTab('quick')}>
          <Zap size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />Quick Send
        </button>
        <button className={`tab ${tab === 'single' ? 'active' : ''}`} onClick={() => setTab('single')}>
          <Send size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />Single Send
        </button>
        <button className={`tab ${tab === 'mass' ? 'active' : ''}`} onClick={() => setTab('mass')}>
          <Users size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />Mass Send
        </button>
      </div>

      {tab === 'quick' && <QuickSend templates={templates} />}
      {tab === 'single' && <SingleSend leads={leads} templates={templates} categories={categories} />}
      {tab === 'mass' && <MassSend leads={leads} templates={templates} categories={categories} />}
    </div>
  );
}